import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Delete, Loader2 } from "lucide-react";
import axios from "axios";
import { API, errMsg, setSession } from "@/lib/api";
import { Logo, BackButton } from "@/components/fly/Logo";

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "clr", "0", "del"];

const KINDS = {
  judge: { list: "/public/judges", login: "/auth/judge-login", body: (n, pin) => ({ judge_number: n, pin }), home: "/sedzia", title: "Panel sędziego i konferansjera" },
  registrar: { list: "/public/registrars", login: "/auth/registrar-login", body: (n, pin) => ({ number: n, pin }), home: "/rejestracja", title: "Rejestracja zawodników" },
  announcer: { list: "/public/announcers", login: "/auth/announcer-login", body: (n, pin) => ({ number: n, pin }), home: "/konferansjer", title: "Panel sędziego i konferansjera" },
  diploma: { list: "/public/diplomas", login: "/auth/diploma-login", body: (n, pin) => ({ number: n, pin }), home: "/dyplomy", title: "Zawody" },
};

export default function JudgeLogin({ kind = "judge" }) {
  const K = KINDS[kind];
  const nav = useNavigate();
  const [judges, setJudges] = useState([]);
  const [num, setNum] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}${K.list}`).then((r) => setJudges(r.data));
  }, [K.list]);

  const press = (k) => {
    if (k === "del") return setPin((p) => p.slice(0, -1));
    if (k === "clr") return setPin("");
    if (k && pin.length < 8) setPin((p) => p + k);
  };

  useEffect(() => {
    if (!num) return;
    const onKey = (e) => {
      if (/^[0-9]$/.test(e.key)) setPin((p) => (p.length < 8 ? p + e.key : p));
      else if (e.key === "Backspace") setPin((p) => p.slice(0, -1));
      else if (e.key === "Escape") setPin("");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [num]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}${K.login}`, K.body(num, pin));
      setSession(data);
      nav(K.home);
    } catch (e) {
      toast.error(errMsg(e));
      setPin("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-md mx-auto px-5 py-10">
      <div className="flex items-center gap-4"><BackButton /><Logo /></div>
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-blue-400 mt-10">{K.title}</p>
      {(kind === "judge" || kind === "announcer") && (
        <div className="grid grid-cols-2 gap-2 mt-4 p-1 rounded-xl bg-[#1C2541]" data-testid="login-role-switch">
          {[["judge", "Sędzia", "/sedzia/login"], ["announcer", "Konferansjer", "/konferansjer/login"]].map(([k, l, to]) => (
            <button key={k} data-testid={`login-role-${k}`} onClick={() => { setNum(null); setPin(""); nav(to); }}
              className={`h-11 rounded-lg font-semibold text-sm transition-colors ${kind === k ? "bg-blue-600 text-white" : "text-slate-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      )}
      <h1 className="font-display text-4xl font-extrabold uppercase mt-2">Wybierz stanowisko</h1>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(70px,1fr))] gap-2 mt-6">
        {judges.map((j) => (
          <button
            key={j.number}
            data-testid={`judge-select-${j.number}`}
            onClick={() => { setNum(j.number); setPin(""); }}
            className={`h-20 rounded-xl border font-display text-3xl font-bold transition-[background-color,border-color] duration-150 ${
              num === j.number ? "bg-blue-600 border-blue-400 text-white" : "bg-[#1C2541] border-blue-500/20 text-slate-300 hover:border-blue-500/50"
            }`}
          >
            {j.number}
            <div className="text-[10px] font-mono font-normal truncate px-1 opacity-80">{j.name}</div>
          </button>
        ))}
      </div>
      {judges.length === 0 && <p data-testid="login-no-users" className="text-sm text-slate-400 mt-6">Brak kont – organizator musi dodać użytkownika w zakładce Użytkownicy.</p>}
      {num && (
        <div className="mt-8 rise">
          <div className="flex justify-center gap-3 h-6" data-testid="judge-pin-dots">
            {Array.from({ length: Math.max(4, pin.length) }).map((_, i) => (
              <span key={i} className={`h-4 w-4 rounded-full border ${i < pin.length ? "bg-blue-500 border-blue-400" : "border-slate-600"}`} />
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-6">
            {KEYS.map((k) => (
                <button
                  key={k}
                  data-testid={`pin-key-${k}`}
                  onClick={() => press(k)}
                  className={`h-16 rounded-xl border active:scale-95 transition-transform flex items-center justify-center gap-2 ${
                    k === "del" || k === "clr" ? "bg-red-500/10 border-red-500/30 text-red-200 hover:bg-red-500/20 font-semibold text-sm" : "bg-white/5 border-white/10 hover:bg-white/10 font-display text-3xl font-semibold"
                  }`}
                >
                  {k === "del" ? <><Delete className="w-5 h-5" />Usuń</> : k === "clr" ? "Wyczyść" : k}
                </button>
            ))}
          </div>
          <button
            data-testid="judge-login-submit"
            disabled={pin.length < 4 || busy}
            onClick={submit}
            className="mt-6 w-full h-14 rounded-xl bg-blue-600 hover:bg-blue-500 font-display text-xl font-bold uppercase tracking-wider shadow-lg shadow-blue-600/30 disabled:opacity-40 transition-colors flex items-center justify-center gap-2"
          >
            {busy && <Loader2 className="w-5 h-5 animate-spin" />} Zaloguj
          </button>
        </div>
      )}
    </main>
  );
}
